import React, { useState, useEffect } from 'react';
import { damageCatalogApi, formatApiError } from '../services/api';
import { Wrench, Plus, Trash, PencilSimple, FloppyDisk } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { isTotalLossItem } from '../lib/damageBilling';

const CATEGORIES = [
  { value: 'lack',       label: 'Lack' },
  { value: 'karosserie', label: 'Karosserie' },
  { value: 'glas',       label: 'Glas' },
  { value: 'reifen',     label: 'Reifen' },
  { value: 'innenraum',  label: 'Innenraum' },
  { value: 'technik',    label: 'Technik' },
  { value: 'reinigung',  label: 'Reinigung' },
  { value: 'sonstige',   label: 'Sonstige' },
];
const CATEGORY_LABELS = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const emptyForm = {
  name: '',
  category: 'sonstige',
  default_cost: '',
  default_deductible: '',
  default_description: '',
  active: true,
};

const formatEur = (v) =>
  v === null || v === undefined || v === ''
    ? '—'
    : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v));

const DamageCatalogCard = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await damageCatalogApi.getAll();
      setItems(res.data || []);
    } catch (err) {
      toast.error(formatApiError(err) || 'Katalog konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || '',
      category: item.category || 'sonstige',
      default_cost: item.default_cost != null ? String(item.default_cost) : '',
      default_deductible: item.default_deductible != null ? String(item.default_deductible) : '',
      default_description: item.default_description || '',
      active: item.active !== false,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Bezeichnung ist erforderlich');
      return;
    }
    const payload = {
      name: form.name.trim(),
      category: form.category,
      default_cost: form.default_cost === '' ? null : Number(form.default_cost),
      default_deductible: form.default_deductible === '' ? null : Number(form.default_deductible),
      default_description: form.default_description || null,
      active: !!form.active,
    };
    try {
      setSaving(true);
      if (editing) {
        await damageCatalogApi.update(editing.id, payload);
      } else {
        await damageCatalogApi.create(payload);
      }
      toast.success(editing ? 'Katalog-Eintrag aktualisiert' : 'Katalog-Eintrag hinzugefügt');
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err) || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`„${item.name}" wirklich löschen?`)) return;
    try {
      await damageCatalogApi.delete(item.id);
      toast.success('Eintrag gelöscht');
      load();
    } catch (err) {
      toast.error(formatApiError(err) || 'Löschen fehlgeschlagen');
    }
  };

  return (
    <div className="card-swiss p-6" data-testid="damage-catalog-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-[#FFB800]/15 rounded-sm flex items-center justify-center">
          <Wrench weight="fill" size={20} className="text-[#FFB800]" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold">Schadenskatalog</h2>
          <p className="text-sm text-[#525866]">
            Vordefinierte Standard-Schäden mit Kosten und Beschreibung. Werden im Schadens-Erfassen-Dialog
            zur schnellen Auswahl angeboten.
          </p>
        </div>
        <Button type="button" onClick={openNew} className="btn-primary" data-testid="damage-catalog-add-btn">
          <Plus size={14} className="mr-1" /> Eintrag hinzufügen
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-[#525866]">Lade Katalog…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-[#8A94A6]">Noch keine Einträge.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-[#525866] border-b border-[#E2E4E9]">
              <tr>
                <th className="py-2 pr-2">Bezeichnung</th>
                <th className="py-2 pr-2">Kategorie</th>
                <th className="py-2 pr-2 text-right">Standard­kosten</th>
                <th className="py-2 pr-2 text-right">Selbstbehalt</th>
                <th className="py-2 pr-2 text-center w-16">Aktiv</th>
                <th className="py-2 pr-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-[#E2E4E9] hover:bg-[#FAFAFB]">
                  <td className="py-2 pr-2">
                    <div className="font-semibold text-[#111418]">{item.name}</div>
                    {item.default_description && (
                      <div className="text-xs text-[#8A94A6] truncate max-w-md">{item.default_description}</div>
                    )}
                  </td>
                  <td className="py-2 pr-2">
                    <span className="text-xs font-semibold uppercase tracking-wide bg-[#F1F2F4] px-2 py-0.5 rounded">
                      {CATEGORY_LABELS[item.category] || item.category}
                    </span>
                  </td>
                  <td className="py-2 pr-2 text-right tabular-nums font-semibold text-[#E60000]">
                    {isTotalLossItem(item) ? 'SB laut Buchung' : formatEur(item.default_cost)}
                  </td>
                  <td className="py-2 pr-2 text-right tabular-nums text-[#525866]">
                    {formatEur(item.default_deductible)}
                  </td>
                  <td className="py-2 pr-2 text-center">
                    <span className={`inline-block w-2 h-2 rounded-full ${item.active ? 'bg-[#009951]' : 'bg-[#8A94A6]'}`} />
                  </td>
                  <td className="py-2 pr-2 text-right">
                    <button
                      type="button"
                      onClick={() => openEdit(item)}
                      className="p-1.5 rounded-sm hover:bg-[#F1F2F4] text-[#525866] mr-1"
                      aria-label="Bearbeiten"
                      data-testid={`damage-catalog-edit-${item.id}`}
                    >
                      <PencilSimple size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(item)}
                      className="p-1.5 rounded-sm hover:bg-red-50 text-[#525866] hover:text-[#E60000]"
                      aria-label="Löschen"
                      data-testid={`damage-catalog-delete-${item.id}`}
                    >
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Katalog-Eintrag bearbeiten' : 'Neuen Katalog-Eintrag anlegen'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="overline">Bezeichnung *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-swiss"
                required
                data-testid="damage-catalog-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Kategorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="input-swiss" data-testid="damage-catalog-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end pb-2">
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    className="w-4 h-4 accent-[#009951]"
                    data-testid="damage-catalog-active-checkbox"
                  />
                  Aktiv (im Picker sichtbar)
                </label>
              </div>
            </div>

            {isTotalLossItem(form) && <p className="text-sm p-3 bg-blue-50 border border-blue-200 rounded-sm">Totalschaden: Die Rechnung enthält nur die SB aus der Buchung, ersatzweise aus dem Fahrzeug. Standardkosten werden nicht zusätzlich berechnet.</p>}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Standard­kosten (€)</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={form.default_cost}
                  onChange={(e) => setForm({ ...form, default_cost: e.target.value })}
                  className="input-swiss"
                  placeholder="z. B. 150"
                  data-testid="damage-catalog-cost-input"
                />
              </div>
              <div>
                <Label className="overline">Standard­selbst­behalt (€)</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={form.default_deductible}
                  onChange={(e) => setForm({ ...form, default_deductible: e.target.value })}
                  className="input-swiss"
                  placeholder="z. B. 500"
                />
                <p className="text-xs text-[#8A94A6] mt-1">Leer = aus Buchung übernehmen</p>
              </div>
            </div>

            <div>
              <Label className="overline">Standard­beschreibung</Label>
              <textarea
                value={form.default_description}
                onChange={(e) => setForm({ ...form, default_description: e.target.value })}
                className="input-swiss w-full px-3 py-2"
                rows={3}
                placeholder="Wird im Schadens-Dialog vorausgefüllt — Mitarbeiter kann sie weiter bearbeiten."
                data-testid="damage-catalog-description-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Abbrechen</Button>
              <Button type="submit" disabled={saving} className="btn-primary" data-testid="damage-catalog-save-btn">
                <FloppyDisk size={14} className="mr-1" />
                {saving ? 'Speichere…' : (editing ? 'Speichern' : 'Hinzufügen')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DamageCatalogCard;
